import { useEffect, useState } from "react";
import { Check, X, ArrowRight } from "lucide-react";
import { api, formatApiError } from "../lib/api";

export default function WaitlistModal({
  slug = "sample-library",
  name = "Sample Library",
  triggerLabel = "Join the Waitlist",
  triggerClassName = "",
  triggerTestid = "waitlist-modal-trigger",
  heading = "Join the Sample Library waitlist",
  supportingText = "Be the first to know when the Sample Library launches and new products become available.",
  children,
}) {
  const [open, setOpen] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState({ state: "idle", msg: "" });

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    if (!firstName || !email) return;
    setStatus({ state: "loading", msg: "" });
    try {
      const { data } = await api.post("/waitlist", {
        product_slug: slug,
        product_name: name,
        full_name: firstName,
        email,
      });
      setStatus({ state: "done", msg: data.message });
      // Analytics: launch-list / waitlist signup (no PII, no purchase data).
      try {
        if (window.posthog) window.posthog.capture("launch_list_signup", { product_slug: slug });
      } catch (e) {}
    } catch (err) {
      setStatus({ state: "error", msg: formatApiError(err.response?.data?.detail) });
    }
  };

  const field = "w-full bg-surface border border-border px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none transition-colors";

  return (
    <>
      {children ? (
        <span onClick={() => setOpen(true)} data-testid={triggerTestid} className={triggerClassName} role="button" tabIndex={0}
          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") setOpen(true); }}>
          {children}
        </span>
      ) : (
        <button type="button" data-testid={triggerTestid} onClick={() => setOpen(true)} className={triggerClassName}>
          {triggerLabel}
        </button>
      )}

      {open && (
        <div data-testid="waitlist-modal" className="fixed inset-0 z-[100] flex items-center justify-center p-4" role="dialog" aria-modal="true">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setOpen(false)} />
          <div className="relative w-full max-w-md bg-base border border-border shadow-2xl">
            <span aria-hidden className="absolute -top-px -left-px h-3.5 w-3.5 border-t-2 border-l-2 border-primary/60" />
            <span aria-hidden className="absolute -top-px -right-px h-3.5 w-3.5 border-t-2 border-r-2 border-primary/60" />
            <span aria-hidden className="absolute -bottom-px -left-px h-3.5 w-3.5 border-b-2 border-l-2 border-primary/60" />
            <span aria-hidden className="absolute -bottom-px -right-px h-3.5 w-3.5 border-b-2 border-r-2 border-primary/60" />
            <button type="button" data-testid="waitlist-modal-close" onClick={() => setOpen(false)}
              className="absolute top-3 right-3 text-muted-foreground hover:text-foreground transition-colors" aria-label="Close">
              <X className="h-5 w-5" />
            </button>

            {status.state === "done" ? (
              <div data-testid="waitlist-modal-success" className="p-7">
                <div className="flex items-center gap-2 text-primary">
                  <Check className="h-5 w-5" /><span className="font-display text-lg">You're on the list</span>
                </div>
                <p className="mt-2 text-sm text-foreground/65 leading-relaxed">{status.msg}</p>
              </div>
            ) : (
              <form onSubmit={submit} data-testid="waitlist-modal-form" className="p-7">
                <p className="mono-label">Coming soon</p>
                <h3 className="mt-2 font-display text-xl font-medium leading-snug">{heading}</h3>
                <p className="mt-2 text-sm text-foreground/60 leading-relaxed">{supportingText}</p>
                <div className="mt-5 space-y-3">
                  <input data-testid="waitlist-modal-name" required value={firstName} onChange={(e) => setFirstName(e.target.value)} placeholder="First name" className={field} />
                  <input data-testid="waitlist-modal-email" required type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email address" className={field} />
                </div>
                <p data-testid="waitlist-modal-consent" className="mt-3 text-xs text-muted-foreground leading-relaxed">
                  By joining, you'll receive launch updates for {name}. No purchase is required and no payment is taken now. Unsubscribe anytime.
                </p>
                {status.state === "error" && <p data-testid="waitlist-modal-error" className="mt-3 text-xs text-destructive">{status.msg}</p>}
                <button type="submit" disabled={status.state === "loading"} data-testid="waitlist-modal-submit"
                  className="group mt-5 w-full inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground text-sm font-medium px-6 py-3.5 hover:bg-primary-hover transition-colors disabled:opacity-60 shadow-[0_0_20px_rgba(0,229,153,0.3)]">
                  {status.state === "loading" ? "Sending…" : "Join the Waitlist"}
                  <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </button>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
}
